import React, { useMemo, useState } from 'react';

import { Alert, AlertTitle, Box, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import {
  useBlackoutsContext,
  useForecastingContext,
  usePackageOptionsContext,
  usePropertySetupContext,
} from '../../PricingToolSessionContext';
import { usePricingToolTabCount } from '../../PricingToolTabCounts';
import { PRICING_OPTION_LABELS } from '../../constants';
import SectionLabel from '../../shared/SectionLabel';
import { BASIC_CATEGORY_INDEX, SOLO_CATEGORY_INDEX, UPGRADE_CATEGORY_INDEX } from '../packageOptions/packageCategories';

import AllocationsTable from './AllocationsTable';
import ForecastInputsGrid from './ForecastInputsGrid';
import ForecastMetricsTable from './ForecastMetricsTable';
import type { ForecastOptionColumn } from './ForecastMetricsTable';
import { computeAvailableTravelDays } from './availableTravelDates';
import { computeForecastTotals } from './computeForecastTotals';
import { computeMaterialisation } from './computeMaterialisation';
import {
  DEFAULT_SOLO_RATE_PCT,
  DEFAULT_UPGRADE_RATE_PCT,
  EMPTY_OPTION_INPUTS,
  hasCounterpartForAnyBaseRoom,
  parseMoney,
  parsePercentWithDefault,
  resolveEffectivePackages,
} from './forecastingModel';

export default function ForecastingTab() {
  // Selection is shared with Package Options; a local one would show a different option's grids.
  const { activeOption, optionStates, setActiveOption } = usePackageOptionsContext();
  const { session, setPackages, setField } = useForecastingContext();
  const { values: propertySetup, fx } = usePropertySetupContext();
  const { blackouts } = useBlackoutsContext();
  const [subTab, setSubTab] = useState(0);

  const { currency: contractCurrency, rate: exchangeRate } = fx;
  // A non-positive rate counts as not loaded, so no margin is published from it. Matches convertAmountToAud.
  const audToContractRate = exchangeRate !== undefined && exchangeRate > 0 ? exchangeRate : undefined;

  const optionIndexes = useMemo(
    () =>
      Object.keys(optionStates)
        .map(Number)
        .sort((a, b) => a - b),
    [optionStates],
  );

  const firstOptionInputs = session.optionInputs[0] ?? EMPTY_OPTION_INPUTS;

  const options = useMemo(
    () =>
      optionIndexes.map((optionIndex) => {
        const inputs = session.optionInputs[optionIndex] ?? EMPTY_OPTION_INPUTS;
        const effectivePackages = resolveEffectivePackages(inputs, optionIndex, firstOptionInputs);
        const optionState = optionStates[optionIndex];

        return {
          effectivePackages,
          hasSoloRooms: hasCounterpartForAnyBaseRoom(
            optionState.roomsByCategory[BASIC_CATEGORY_INDEX],
            optionState.roomsByCategory[SOLO_CATEGORY_INDEX],
          ),
          hasUpgradeRooms: hasCounterpartForAnyBaseRoom(
            optionState.roomsByCategory[BASIC_CATEGORY_INDEX],
            optionState.roomsByCategory[UPGRADE_CATEGORY_INDEX],
          ),
          index: optionIndex,
          inputs,
          name: PRICING_OPTION_LABELS[optionIndex],
          rooms: optionState.roomsByCategory[BASIC_CATEGORY_INDEX].filter((room) => room.assetId),
          totals:
            audToContractRate === undefined
              ? undefined
              : computeForecastTotals({
                  audToContractRate,
                  expectedAvgLos: inputs.expectedAvgLos.trim() === '' ? null : parseMoney(inputs.expectedAvgLos),
                  lengthsOfStay: optionState.lengthsOfStay,
                  packagesByAssetLos: effectivePackages,
                  pricingByRoomId: optionState.pricingByRoomId,
                  roomsByCategory: optionState.roomsByCategory,
                  soloRatePct: parsePercentWithDefault(inputs.soloRatePct, DEFAULT_SOLO_RATE_PCT),
                  upgradeRatePct: parsePercentWithDefault(inputs.upgradeRatePct, DEFAULT_UPGRADE_RATE_PCT),
                }),
          optionState,
        };
      }),
    [audToContractRate, firstOptionInputs, optionIndexes, optionStates, session.optionInputs],
  );

  const forecastCells = useMemo(
    () =>
      Object.values(session.optionInputs).reduce(
        (total, option) =>
          total + Object.values(option.packagesByAssetLos).filter((value) => parseMoney(value) > 0).length,
        0,
      ),
    [session.optionInputs],
  );
  usePricingToolTabCount('forecasting', forecastCells || undefined);

  const columns: Array<ForecastOptionColumn> = options.flatMap((option) =>
    option.totals ? [{ index: option.index, name: option.name, totals: option.totals }] : [],
  );

  const pricingGapsByOption = options.flatMap((option) => {
    const { unpricedCells = [], unpricedExtraNightRooms = [] } = option.totals ?? {};
    const cells = unpricedCells.map((cell) => `${cell.assetName} (${cell.nights}N)`).join(', ');
    const parts = [
      cells && `no rate for ${cells}`,
      unpricedExtraNightRooms.length && `no extra-night rate for ${unpricedExtraNightRooms.join(', ')}`,
    ].filter(Boolean);

    return parts.length ? [`${option.name}: ${parts.join('; ')}`] : [];
  });

  const hasRooms = options.some((option) => option.rooms.length > 0);

  const activeIndex = optionIndexes.includes(activeOption) ? activeOption : (optionIndexes[0] ?? 0);
  const active = options.find((option) => option.index === activeIndex);

  const travelDays = useMemo(
    () => computeAvailableTravelDays(propertySetup?.travelFrom, propertySetup?.travelTo, blackouts),
    [blackouts, propertySetup?.travelFrom, propertySetup?.travelTo],
  );

  const materialisationRows = active
    ? computeMaterialisation({
        allocationByRoomId: Object.fromEntries(
          Object.entries(active.optionState.pricingByRoomId).map(([roomId, pricing]) => [roomId, pricing?.allocation]),
        ),
        availableDays: travelDays?.availableDays ?? null,
        expectedAvgLos: active.inputs.expectedAvgLos.trim() === '' ? null : parseMoney(active.inputs.expectedAvgLos),
        lengthsOfStay: active.optionState.lengthsOfStay,
        packagesByAssetLos: active.effectivePackages,
        rooms: active.rooms,
      })
    : [];

  return (
    <TabPanelShell
      subtitle="Forecast the packages you expect to sell per option and compare the resulting revenue and margin. Figures stay in your session - writing the forecast back to Salesforce is not part of this tab."
      title="Forecasting"
    >
      {!hasRooms && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Add room types and prices on the Package Options tab first. The forecast prices each package from those grids,
          so there is nothing to forecast until at least one room type carries an asset.
        </Alert>
      )}

      {audToContractRate === undefined && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Waiting on the {contractCurrency} exchange rate. Net rates are contracted in {contractCurrency}, so the
          summary stays blank until it loads.
        </Alert>
      )}

      <ToggleButtonGroup
        exclusive
        onChange={(_event, next) => next !== null && setActiveOption(next)}
        size="small"
        sx={{ mb: 3 }}
        value={activeIndex}
      >
        {options.map((option) => (
          <ToggleButton key={option.index} value={option.index}>
            {option.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Tabs
        onChange={(_event, next) => setSubTab(next)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        value={subTab}
      >
        <Tab label="Forecasted packages" />
        <Tab label="Allocations & materialisation" />
      </Tabs>

      {subTab === 1 && (
        <Box>
          <SectionLabel>Allocations & materialisation</SectionLabel>

          {propertySetup === null && (
            <Alert severity="info">
              Waiting on the offer&apos;s travel dates. Materialisation needs a travel window to count against, so this
              stays blank until they load.
            </Alert>
          )}

          {propertySetup !== null && travelDays === null && (
            <Alert severity="info">
              This offer has no travel dates, so there is no window to measure the forecast against. They come from the
              opportunity&apos;s deals and are read-only here.
            </Alert>
          )}

          {travelDays !== null && <AllocationsTable rows={materialisationRows} travelDays={travelDays} />}
        </Box>
      )}

      {subTab === 0 && active && (
        <Box sx={{ mb: 4 }}>
          <SectionLabel>Forecasted packages</SectionLabel>

          {active.rooms.length ? (
            <>
              <Typography color="text.secondary" sx={{ mb: 2, mt: -1 }} variant="body2">
                How many packages you expect to sell, per room type and stay length.
              </Typography>

              <ForecastInputsGrid
                baseAvgLos={active.totals?.baseAvgLos ?? null}
                effectivePackages={active.effectivePackages}
                hasSoloRooms={active.hasSoloRooms}
                hasUpgradeRooms={active.hasUpgradeRooms}
                inputs={active.inputs}
                isFirstOption={active.index === 0}
                lengthsOfStay={active.optionState.lengthsOfStay}
                onFieldChange={(field, value) => setField(active.index, field, value)}
                onPackagesChange={(assetId, nights, value) => setPackages(active.index, assetId, nights, value)}
                rooms={active.rooms}
              />
            </>
          ) : (
            <Typography color="text.secondary" variant="body2">
              No room types with a linked asset on this option yet.
            </Typography>
          )}
        </Box>
      )}

      {subTab === 0 && columns.length > 0 && (
        <Box>
          <SectionLabel>Forecast summary</SectionLabel>

          {pricingGapsByOption.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Some forecast nights carry no rate yet</AlertTitle>
              These nights still count towards the totals, so TTV, vendor revenue and the blended ADR read low until the
              Package Options tab holds a rate for them.
              <Stack sx={{ mt: 1 }}>
                {pricingGapsByOption.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <ForecastMetricsTable columns={columns} contractCurrency={contractCurrency} />
        </Box>
      )}
    </TabPanelShell>
  );
}
